import React, { useCallback, useEffect, useMemo, useState } from "react";
import LaddaButton from "../../../components/buttons/laddaButton";
import SimpleTable from "../../../components/tables/simpleTable";
import { icons } from "../../../constants";
import {
  callActionApiAsync,
  callApiAsync,
  getBiomedicalEquipmentModelAccessories,
  getCallAccessoryData,
  postDeleteBiomedicalEquipmentModelAccessory
} from "../../../services/apiService";
import ModelAccessoriesModal from "./ModelAccessoriesModal";

const modelAccessoryColumns = [
  { field: "accessory", header: "Accesorio" },
  {
    field: "actionButtons",
    header: "",
    columnType: "actionButtons"
  }
];

const ModelAccessoriesTab = ({
  modelRow,
  userId,
  isCatalogAdmin,
  pushToast
}) => {
  const [accessoryRows, setAccessoryRows] = useState([]);
  const [accessoryOptions, setAccessoryOptions] = useState([]);
  const [openAccessoryModal, setOpenAccessoryModal] = useState(false);
  const [selectedAccessoryRow, setSelectedAccessoryRow] = useState(null);

  const selectedModelId = modelRow?.value ? Number(modelRow.value) : null;
  const selectedBrandId = modelRow?.brandId ? Number(modelRow.brandId) : null;

  const loadAccessoryTemplate = useCallback(() => {
    if (!selectedModelId || !userId) {
      setAccessoryRows([]);
      return;
    }

    callApiAsync(
      () => getBiomedicalEquipmentModelAccessories(selectedModelId, userId),
      (data) => {
        setAccessoryRows(data?.table || []);
      }
    );
  }, [selectedModelId, userId]);

  const loadAccessoryOptions = useCallback(() => {
    if (!selectedBrandId) {
      setAccessoryOptions([]);
      return;
    }

    callApiAsync(
      () => getCallAccessoryData(selectedBrandId, 0),
      (data) => {
        setAccessoryOptions(data?.list || []);
      }
    );
  }, [selectedBrandId]);

  useEffect(() => {
    if (!selectedModelId) {
      setAccessoryRows([]);
      return;
    }

    loadAccessoryTemplate();
  }, [loadAccessoryTemplate, selectedModelId]);

  useEffect(() => {
    loadAccessoryOptions();
  }, [loadAccessoryOptions]);

  const handleDeleteAccessory = useCallback(
    async (row) => {
      if (!row?.id || !userId) return;
      if (
        !window.confirm(
          `¿Eliminar el accesorio "${row.accessory}" del modelo seleccionado?`
        )
      ) {
        return;
      }

      await callActionApiAsync(
        () =>
          postDeleteBiomedicalEquipmentModelAccessory({
            id: row.id,
            userId
          }),
        () => {
          loadAccessoryTemplate();
          pushToast(
            "success",
            "Accesorios por modelo",
            "El accesorio se eliminó de la plantilla del modelo."
          );
        },
        (error) => {
          pushToast(
            "error",
            "Accesorios por modelo",
            typeof error === "string" && error
              ? error
              : "No se pudo eliminar el accesorio del modelo."
          );
        }
      );
    },
    [loadAccessoryTemplate, pushToast, userId]
  );

  const tableButtons = useMemo(
    () =>
      isCatalogAdmin
        ? [
            {
              type: "icon",
              icon: icons.editYellow,
              onClick: (row) => {
                setSelectedAccessoryRow(row);
                setOpenAccessoryModal(true);
              }
            },
            {
              type: "icon",
              icon: icons.trash,
              onClick: handleDeleteAccessory
            }
          ]
        : [],
    [handleDeleteAccessory, isCatalogAdmin]
  );

  if (!selectedModelId) {
    return (
      <div className="admin-catalog-detail-card">
        <p className="admin-catalog-detail-value admin-catalog-hint">
          Seleccione una marca y un modelo para administrar los accesorios por
          defecto.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="admin-catalog-detail-card">
        <p className="admin-catalog-detail-label">Modelo seleccionado</p>
        <p className="admin-catalog-detail-value">{modelRow?.name || "—"}</p>
        <p className="admin-catalog-hint">
          Aquí solo se define qué accesorios aplica el modelo. El número de parte y
          la cantidad se diligencian después en la hoja de vida del equipo.
        </p>
      </div>

      <div className="mt-25">
        <div className="IHContainer j-space-between mb-15">
          <p className="createRist-title ml-0">Accesorios por defecto del modelo</p>
          {isCatalogAdmin && (
            <LaddaButton
              width={"170px"}
              fontSize={"12px"}
              height={"40px"}
              variant="contained"
              margin={"0"}
              onClick={() => {
                setSelectedAccessoryRow(null);
                setOpenAccessoryModal(true);
              }}
            >
              <span className="HContainer align-center">
                <img className="btn-icon mr-10" src={icons.plusIcon} alt="icon" />
                Nuevo accesorio
              </span>
            </LaddaButton>
          )}
        </div>

        <SimpleTable
          data={accessoryRows}
          columns={modelAccessoryColumns}
          scrollHeight={"280px"}
          text={"Accesorios del modelo"}
          buttons={tableButtons}
          emptyMessage="No hay accesorios configurados para este modelo."
        />
      </div>

      <ModelAccessoriesModal
        open={openAccessoryModal}
        close={() => {
          setOpenAccessoryModal(false);
          setSelectedAccessoryRow(null);
        }}
        modelId={selectedModelId}
        brandId={selectedBrandId}
        userId={userId}
        accessoryOptions={accessoryOptions}
        setAccessoryOptions={setAccessoryOptions}
        selectedRow={selectedAccessoryRow}
        onSaved={() => {
          loadAccessoryTemplate();
          loadAccessoryOptions();
          pushToast(
            "success",
            "Accesorios por modelo",
            selectedAccessoryRow
              ? "El accesorio del modelo se actualizó correctamente."
              : "El accesorio se agregó correctamente a la plantilla del modelo."
          );
        }}
        isViewer={!isCatalogAdmin}
      />
    </>
  );
};

export default ModelAccessoriesTab;
